import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageSquare, CheckCircle2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import usePageMeta from "@/hooks/usePageMeta";

export default function Contact() {
  const { user } = useAuth();
  const { settings } = usePublicSettings();
  const supportEmail = settings.CONTACT_EMAIL_SUPPORT || "support@colourmatch.au";
  usePageMeta({
    title: "Contact — ColourMatch AI Paint Visualiser",
    description: "Contact the ColourMatch team — the AI paint visualiser software for Australian homeowners. We typically reply within one business day.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const setF = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/support", form);
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="contact-success">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
          <CheckCircle2 className="mx-auto text-brand-teal mb-5" size={48} />
          <h1 className="text-3xl font-bold tracking-tight mb-2">Message sent</h1>
          <p className="text-sm text-brand-muted">
            Thanks {form.name || "there"} — we'll reply to <strong>{form.email}</strong> within 2 business days.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12 lg:py-20" data-testid="contact-page">
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="text-center max-w-xl mx-auto mb-12"
      >
        <span className="text-xs font-semibold tracking-widest uppercase text-brand-teal">Contact</span>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-brand-ink">
          Get in touch.
        </h1>
        <p className="mt-4 text-brand-muted">
          Questions, feedback, refund requests or render trouble — we're here.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-1 space-y-3">
          <InfoCard icon={<Mail size={18} />} title="Email" value={supportEmail} sub="Replies within 2 business days" />
          <InfoCard icon={<MessageSquare size={18} />} title="What to include" value="Project title + screenshots" sub="Helps us help you faster" />
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }}
          className="md:col-span-2 bg-white rounded-3xl p-6 lg:p-8 shadow-soft space-y-4"
          data-testid="contact-form"
        >
          {err && (
            <div className="bg-destructive/5 border border-destructive/20 text-destructive rounded-2xl px-4 py-3 text-sm" data-testid="contact-error">{err}</div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" required value={form.name} onChange={(v) => setF("name", v)} testid="contact-name-input" />
            <Field label="Email" type="email" required value={form.email} onChange={(v) => setF("email", v)} testid="contact-email-input" />
          </div>
          <Field label="Subject" required value={form.subject} onChange={(v) => setF("subject", v)} testid="contact-subject-input" placeholder="e.g. Refund request — project ‘Living room’" />
          <div>
            <label className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-2 block">Message</label>
            <textarea
              required rows={6} value={form.message} onChange={(e) => setF("message", e.target.value)}
              data-testid="contact-message-input"
              className="w-full bg-white border border-brand-border rounded-2xl px-4 py-3 text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition resize-none"
              placeholder="Tell us what's going on…"
            />
          </div>
          <button type="submit" disabled={busy} data-testid="contact-submit-btn"
            className="w-full h-14 bg-brand-navy text-white rounded-2xl text-base font-semibold hover:bg-brand-navy/90 hover:shadow-navy disabled:opacity-60 transition-all tap-spring flex items-center justify-center gap-2">
            {busy ? "Sending…" : (<>Send message <ArrowRight size={18} /></>)}
          </button>
        </motion.form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-2 block">{label}</label>
      <input
        type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}
        className="w-full h-14 bg-white border border-brand-border rounded-2xl px-4 text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
      />
    </div>
  );
}

function InfoCard({ icon, title, value, sub }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-soft">
      <div className="w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-3">{icon}</div>
      <div className="text-xs font-semibold text-brand-muted uppercase tracking-widest">{title}</div>
      <div className="text-sm font-semibold text-brand-ink mt-1">{value}</div>
      {sub && <div className="text-xs text-brand-muted mt-1">{sub}</div>}
    </div>
  );
}
